import React, { useState } from 'react';
import { Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton, SearchBox, IconButton, Text, Stack, Checkbox } from '@fluentui/react';
import { FiGrid, FiBarChart2, FiPieChart, FiTrendingUp, FiActivity, FiClock, FiUsers, FiPhone, FiCheckCircle, FiXCircle, FiFolder, FiRotateCcw, FiTarget, FiRepeat, FiMail, FiInbox, FiCornerUpLeft, FiMessageSquare, FiMessageCircle, FiTag, FiList, FiStar, FiThumbsUp, FiHelpCircle } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import './WidgetCatalog.css';

// Catalogue de widgets disponibles
export const WIDGET_CATALOG = [
    // ========================================
    // WIDGETS DU DASHBOARD PAR DÉFAUT
    // ========================================
    // Total Calls (Widget par défaut du Dashboard)
    {
        id: 'total-calls',
        name: 'Total Appels',
        description: 'Nombre total d\'appels reçus',
        category: 'dashboard',
        icon: FiPhone,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#0078D4',
        dataSource: {
            queryId: 'TOTAL_CALLS_TODAY'
        }
    },
    // Calls Answered (Widget par défaut du Dashboard)
    {
        id: 'calls-answered',
        name: 'Appels Répondus',
        description: 'Nombre d\'appels pris en charge',
        category: 'dashboard',
        icon: FiCheckCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#107C10',
        dataSource: {
            queryId: 'CALLS_ANSWERED'
        }
    },
    // Calls Abandoned (Widget par défaut du Dashboard)
    {
        id: 'calls-abandoned',
        name: 'Appels Abandonnés',
        description: 'Nombre d\'appels abandonnés',
        category: 'dashboard',
        icon: FiXCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#D13438',
        dataSource: {
            queryId: 'CALLS_ABANDONED'
        }
    },
    // Abandonment Rate (Widget par défaut du Dashboard)
    {
        id: 'abandonment-rate',
        name: 'Taux d\'Abandon',
        description: 'Pourcentage d\'appels abandonnés',
        category: 'dashboard',
        icon: FiXCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#D13438',
        dataSource: {
            queryId: 'ABANDONMENT_RATE'
        }
    },
    // Average Wait Time (Widget par défaut du Dashboard)
    {
        id: 'avg-wait-time',
        name: 'Temps d\'Attente Moyen',
        description: 'Durée moyenne avant réponse',
        category: 'dashboard',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#0078D4',
        dataSource: {
            queryId: 'AVG_WAIT_TIME'
        }
    },
    // Average Handling Time (Widget par défaut du Dashboard)
    {
        id: 'avg-handling-time',
        name: 'Temps de Traitement (AHT)',
        description: 'Average Handling Time',
        category: 'dashboard',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#0078D4',
        dataSource: {
            queryId: 'AVG_HANDLING_TIME'
        }
    },
    // Average Talk Time (Widget par défaut du Dashboard)
    {
        id: 'avg-talk-time',
        name: 'Temps de Conversation',
        description: 'Durée moyenne de conversation',
        category: 'dashboard',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#0078D4',
        dataSource: {
            queryId: 'AVG_TALK_TIME'
        }
    },
    // Service Level (Widget par défaut du Dashboard)
    {
        id: 'service-level',
        name: 'Niveau de Service',
        description: '% répondu en moins de 20s',
        category: 'dashboard',
        icon: FiActivity,
        defaultSize: { w: 3, h: 4 },
        type: 'gauge',
        color: '#0078D4',
        dataSource: {
            queryId: 'SERVICE_LEVEL'
        }
    },
    // First Call Resolution (Widget par défaut du Dashboard)
    {
        id: 'fcr',
        name: 'Résolution 1er Appel (FCR)',
        description: 'First Call Resolution',
        category: 'dashboard',
        icon: FiCheckCircle,
        defaultSize: { w: 4, h: 2 },
        type: 'metric',
        color: '#107C10',
        dataSource: {
            queryId: 'FIRST_CALL_RESOLUTION'
        }
    },
    // Calls by Hour (Widget par défaut du Dashboard)
    {
        id: 'calls-by-hour',
        name: 'Volume d\'Appels par Heure',
        description: 'Distribution horaire',
        category: 'dashboard',
        icon: FiBarChart2,
        defaultSize: { w: 6, h: 5 },
        type: 'bar',
        color: '#0078D4',
        dataSource: {
            queryId: 'CALLS_BY_HOUR'
        }
    },

    // ========================================
    // WIDGETS GÉNÉRIQUES (Catalogue)
    // ========================================
    // Widgets de métriques
    {
        id: 'widget-total-calls',
        name: 'Total Calls',
        description: 'Display total number of calls',
        category: 'metrics',
        icon: FiPhone,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#0078D4',
        dataSource: { queryId: 'total-calls' }
    },
    {
        id: 'widget-answered-calls',
        name: 'Answered Calls',
        description: 'Calls that were answered',
        category: 'metrics',
        icon: FiCheckCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#107C10',
        dataSource: { queryId: 'total-answered-calls' }
    },
    {
        id: 'widget-abandoned-calls',
        name: 'Abandoned Calls',
        description: 'Calls that were abandoned',
        category: 'metrics',
        icon: FiXCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#D13438',
        dataSource: { queryId: 'total-abandoned-calls' }
    },
    {
        id: 'widget-outbound-calls',
        name: 'Outbound Calls',
        description: 'Total outbound calls',
        category: 'metrics',
        icon: FiTrendingUp,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#8764B8',
        dataSource: { queryId: 'total-outbound-calls' }
    },
    {
        id: 'widget-average-wait-time',
        name: 'Average Wait Time',
        description: 'Average time callers wait',
        category: 'metrics',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#FF8C00',
        dataSource: { queryId: 'average-wait-time' },
        // Valeur renvoyée en SECONDES : sans ce format, la carte affichait le nombre brut
        // et l'export Excel sortait « 69 » là où le tableau KPI voisin lit « 00:01:09 ».
        config: { format: 'duration' }
    },
    {
        id: 'widget-service-level',
        name: 'Service Level',
        description: 'Service level percentage',
        category: 'metrics',
        icon: FiActivity,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#00BCF2',
        dataSource: { queryId: 'service-level' }
    },
    {
        id: 'widget-avg-inbound-call-duration',
        name: 'Avg Inbound Duration',
        description: 'Average duration of inbound calls',
        category: 'metrics',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#498205',
        dataSource: { queryId: 'average-inbound-call-duration' },
        // Valeur renvoyée en SECONDES : sans ce format, la carte affichait le nombre brut
        // et l'export Excel sortait « 69 » là où le tableau KPI voisin lit « 00:01:09 ».
        config: { format: 'duration' }
    },
    {
        id: 'widget-avg-outbound-call-duration',
        name: 'Avg Outbound Duration',
        description: 'Average duration of outbound calls',
        category: 'metrics',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#8E8CD8',
        dataSource: { queryId: 'average-outbound-call-duration' },
        // Valeur renvoyée en SECONDES : sans ce format, la carte affichait le nombre brut
        // et l'export Excel sortait « 69 » là où le tableau KPI voisin lit « 00:01:09 ».
        config: { format: 'duration' }
    },
    {
        id: 'widget-transferred-calls',
        name: 'Transferred Calls',
        description: 'Number of transferred calls',
        category: 'metrics',
        icon: FiActivity,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#8764B8',
        dataSource: { queryId: 'total-transferred-calls' }
    },
    {
        id: 'widget-human-transfer-rate',
        name: 'Human Transfer Rate',
        description: '% of answered calls transferred by an agent to a human',
        category: 'metrics',
        icon: FiUsers,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#107C10',
        dataSource: {
            queryId: 'agent-transfer-rate'
        }
    },
    {
        id: 'widget-flow-transfer-rate',
        name: 'Flow Transfer Rate',
        description: '% of calls transferred by the flow (routing / fallback)',
        category: 'metrics',
        icon: FiTrendingUp,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#0078D4',
        dataSource: {
            queryId: 'flow-transfer-rate'
        }
    },
    {
        id: 'widget-failed-transfers',
        name: 'Failed Transfers',
        description: 'Calls with a failed agent transfer (failed / timeout / abandoned)',
        category: 'metrics',
        icon: FiXCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#D13438',
        dataSource: {
            queryId: 'failed-transfers'
        }
    },
    {
        id: 'widget-waiting-time',
        name: 'Waiting Time Limits',
        description: 'Configured waiting time thresholds',
        category: 'thresholds',
        icon: FiClock,
        defaultSize: { w: 4, h: 2 },
        type: 'metric',
        color: '#FF8C00',
        dataSource: { queryId: 'max-waiting-time' },
        // Valeur renvoyée en SECONDES : sans ce format, la carte affichait le nombre brut
        // et l'export Excel sortait « 69 » là où le tableau KPI voisin lit « 00:01:09 ».
        config: { format: 'duration' }
    },
    {
        id: 'widget-talking-time',
        name: 'Talking Time Limits',
        description: 'Configured talking time thresholds',
        category: 'thresholds',
        icon: FiClock,
        defaultSize: { w: 4, h: 2 },
        type: 'metric',
        color: '#0078D4',
        dataSource: { queryId: 'max-talking-time' },
        // Valeur renvoyée en SECONDES : sans ce format, la carte affichait le nombre brut
        // et l'export Excel sortait « 69 » là où le tableau KPI voisin lit « 00:01:09 ».
        config: { format: 'duration' }
    },
    {
        id: 'widget-abandon-time',
        name: 'Abandon Time Limits',
        description: 'Configured abandon time thresholds',
        category: 'thresholds',
        icon: FiClock,
        defaultSize: { w: 4, h: 2 },
        type: 'metric',
        color: '#D13438',
        dataSource: { queryId: 'max-abandon-time' },
        // Valeur renvoyée en SECONDES : sans ce format, la carte affichait le nombre brut
        // et l'export Excel sortait « 69 » là où le tableau KPI voisin lit « 00:01:09 ».
        config: { format: 'duration' }
    },

    // Widgets de graphiques
    {
        id: 'widget-call-volume-hourly',
        name: 'Call Volume (Hourly)',
        description: 'Hourly call volume distribution',
        category: 'charts',
        icon: FiBarChart2,
        defaultSize: { w: 6, h: 4 },
        type: 'bar',
        color: '#0078D4',
        dataSource: { queryId: 'call-volume-by-hour' }
    },
    {
        id: 'widget-call-volume-daily',
        name: 'Call Volume (Daily)',
        description: 'Daily call volume trends',
        category: 'charts',
        icon: FiTrendingUp,
        defaultSize: { w: 6, h: 4 },
        type: 'line',
        color: '#107C10',
        dataSource: { queryId: 'call-volume-by-day' }
    },
    {
        id: 'widget-call-status-distribution',
        name: 'Call Status Distribution',
        description: 'Distribution by call status',
        category: 'charts',
        icon: FiPieChart,
        defaultSize: { w: 4, h: 4 },
        type: 'donut',
        color: '#8764B8',
        dataSource: { queryId: 'call-status-distribution' }
    },
    {
        id: 'widget-thresholds-distribution',
        name: 'Call Response Distribution',
        description: 'Distribution of calls by response speed',
        category: 'charts',
        icon: FiPieChart,
        defaultSize: { w: 4, h: 4 },
        type: 'donut',
        color: '#107C10',
        dataSource: { queryId: 'response-time-distribution' }
    },
    {
        id: 'widget-calls-by-flow',
        name: 'Calls by Flow',
        description: 'Call distribution by flow',
        category: 'charts',
        icon: FiBarChart2,
        defaultSize: { w: 4, h: 4 },
        type: 'bar',
        color: '#FF8C00',
        dataSource: { queryId: 'calls-by-flow' }
    },
    {
        id: 'widget-calls-by-country',
        name: 'Calls by Country',
        description: 'Call distribution by country',
        category: 'charts',
        icon: FiPieChart,
        defaultSize: { w: 4, h: 4 },
        type: 'donut',
        color: '#00A4A6',
        dataSource: { queryId: 'calls-by-country' }
    },
    {
        id: 'widget-ia-escalation-destinations',
        name: 'IA – Destinations d\'escalade',
        description: 'Files vers lesquelles l\'agent IA escalade les appels',
        category: 'charts',
        icon: FiBarChart2,
        defaultSize: { w: 4, h: 4 },
        type: 'bar',
        color: '#8764B8',
        dataSource: { queryId: 'ia-escalation-destinations' },
        config: { valueFormat: 'number', yAxisLabel: '', tooltipSuffix: ' appels' }
    },
    {
        id: 'widget-ia-action-breakdown',
        name: 'IA – Répartition des actions',
        description: 'Actions terminales de l\'agent IA (escalade, raccrochage, message, transfert)',
        category: 'charts',
        icon: FiPieChart,
        defaultSize: { w: 4, h: 4 },
        type: 'donut',
        color: '#0078D4',
        dataSource: { queryId: 'ia-action-breakdown' }
    },
    {
        id: 'widget-ia-resolution-rate',
        name: 'IA – Taux de résolution',
        description: 'Part des appels gérés par l\'IA sans escalade humaine',
        category: 'metrics',
        icon: FiCheckCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#107C10',
        dataSource: { queryId: 'ia-resolution-rate' }
    },
    {
        id: 'widget-ia-top-subjects',
        name: 'IA – Top motifs d\'appel',
        description: 'Principaux motifs d\'appel détectés par l\'agent IA',
        category: 'charts',
        icon: FiBarChart2,
        defaultSize: { w: 6, h: 4 },
        type: 'bar',
        color: '#FF8C00',
        dataSource: { queryId: 'ia-top-subjects' },
        config: { valueFormat: 'number', yAxisLabel: '', tooltipSuffix: ' appels' }
    },
    {
        id: 'widget-ia-sentiment',
        name: 'IA – Sentiment des appels',
        description: 'Répartition du sentiment (positif / neutre / négatif / mixte)',
        category: 'charts',
        icon: FiPieChart,
        defaultSize: { w: 4, h: 4 },
        type: 'donut',
        color: '#107C10',
        dataSource: { queryId: 'ia-sentiment' }
    },
    {
        id: 'widget-peak-hours',
        name: 'Peak Hours Analysis',
        description: 'Identify peak call hours',
        category: 'charts',
        icon: FiActivity,
        defaultSize: { w: 12, h: 5 },
        type: 'heatmap',
        color: '#D13438',
        dataSource: { queryId: 'peak-hours-analysis' }
    },
    {
        id: 'widget-calls-per-agent',
        name: 'Calls per Agent',
        description: 'Agent performance metrics',
        category: 'charts',
        icon: FiUsers,
        defaultSize: { w: 6, h: 4 },
        type: 'bar',
        color: '#00BCF2',
        dataSource: { queryId: 'calls-per-agent' }
    },
    {
        id: 'widget-supervised-calls-rate',
        name: 'Supervised calls',
        description: 'Share of calls supervised',
        category: 'metrics',
        icon: FiUsers,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#5B5FC7',
        dataSource: { queryId: 'supervised-calls-rate' }
    },
    {
        id: 'widget-supervision-summary',
        name: 'Supervision by supervisor',
        description: 'Sessions and minutes per supervisor',
        category: 'charts',
        icon: FiUsers,
        defaultSize: { w: 6, h: 4 },
        type: 'bar',
        color: '#5B5FC7',
        dataSource: { queryId: 'supervision-summary' }
    },
    {
        id: 'widget-calls-by-queue',
        name: 'Calls by Queue',
        description: 'Queue distribution',
        category: 'charts',
        icon: FiGrid,
        defaultSize: { w: 6, h: 4 },
        type: 'bar',
        color: '#498205',
        dataSource: { queryId: 'calls-by-queue' }
    },

    // Widget KPI Summary by Flow
    {
        id: 'widget-queue-kpi-summary',
        name: 'Flow KPI Summary',
        description: 'Comprehensive KPI metrics by flow (transposed view)',
        category: 'analytics',
        icon: FiGrid,
        defaultSize: { w: 12, h: 6 },
        type: 'table',
        color: '#0078D4',
        dataSource: {
            queryId: 'queue-kpi-summary'
        }
    },

    // Widget Daily half-hour breakdown (template UCCX)
    {
        id: 'widget-daily-halfhour-breakdown',
        name: 'Daily (per 30 min)',
        description: 'Per 30-min slot: queue/agent overflow, abandons, served without queuing, calls in open state',
        category: 'analytics',
        icon: FiGrid,
        defaultSize: { w: 8, h: 8 },
        type: 'table',
        color: '#8A2BE2',
        dataSource: {
            queryId: 'daily-halfhour-breakdown'
        }
    },
    {
        id: 'widget-month-to-date-summary',
        name: 'Month-to-Date Summary',
        description: 'Month-to-date KPI summary with service level, answer rate, and performance metrics by flow',
        category: 'analytics',
        icon: FiClock,
        defaultSize: { w: 12, h: 7 },
        type: 'table',
        color: '#00B294',
        dataSource: {
            queryId: 'month-to-date-summary'
        }
    },
    // Widget Overflow by reason (template Report-4) — matrice raison × file
    {
        id: 'widget-overflow-by-reason',
        name: 'Overflow by Reason',
        description: 'Overflow counts per queue by cause: no agent available, timeout, queue full, out-of-hours, bank holiday',
        category: 'analytics',
        icon: FiGrid,
        defaultSize: { w: 12, h: 5 },
        type: 'table',
        color: '#F7A600',
        dataSource: {
            queryId: 'overflow-by-reason'
        }
    },
    // Même rapport que le précédent, colonnes = flows. Utile quand le débordement se décide
    // AVANT la file (hors horaires, jour férié) : ces appels n'ont pas de file, et la version
    // « par file » les range sous un repli FLOW_NAME peu lisible.
    {
        id: 'widget-overflow-by-reason-flow',
        name: 'Overflow by Reason (by Flow)',
        description: 'Overflow counts per flow by cause: no agent available, timeout, queue full, out-of-hours, bank holiday',
        category: 'analytics',
        icon: FiGrid,
        defaultSize: { w: 12, h: 6 },
        type: 'table',
        color: '#F7A600',
        dataSource: {
            queryId: 'overflow-by-reason-flow'
        }
    },
    {
        id: 'widget-calls-served-time-ranges',
        name: 'Call Served by Time Ranges',
        description: 'Distribution of served calls across time ranges by flow',
        category: 'analytics',
        icon: FiCheckCircle,
        defaultSize: { w: 12, h: 5 },
        type: 'table',
        color: '#107C10',
        dataSource: {
            queryId: 'calls-served-by-time-ranges'
        }
    },
    {
        id: 'widget-calls-abandoned-time-ranges',
        name: 'Abandoned Calls by Time Ranges',
        description: 'Distribution of abandoned calls across time ranges by flow',
        category: 'analytics',
        icon: FiXCircle,
        defaultSize: { w: 12, h: 5 },
        type: 'table',
        color: '#D13438',
        dataSource: {
            queryId: 'calls-abandoned-by-time-ranges'
        }
    },
    {
        id: 'widget-daily-sla-stats',
        name: 'Daily SLA Statistics',
        description: 'Daily served & redirected calls with time-bucket distribution and cumulative SLA percentages',
        category: 'analytics',
        icon: FiActivity,
        defaultSize: { w: 12, h: 7 },
        type: 'sla-table',
        color: '#0078D4',
        dataSource: {
            queryId: 'daily-sla-stats'
        }
    },
    // Jumeau « abandons » du widget SLA ci-dessus. Il existait dans le dashboard par défaut
    // (dashboardConfigs.js) mais PAS ici : absent du catalogue, il était introuvable dans la
    // liste des widgets, et l'édition d'une instance existante ouvrait un panneau vide
    // (AnalyticsDashboard cherche WIDGET_CATALOG.find(...) → undefined → panneau non rendu),
    // donc aucun filtre personnalisé possible.
    {
        id: 'widget-daily-abandon-stats',
        name: 'Daily Abandon Statistics',
        description: 'Daily abandoned calls with time-bucket distribution and cumulative abandon percentages',
        category: 'analytics',
        icon: FiXCircle,
        defaultSize: { w: 12, h: 10 },
        type: 'abandon-table',
        color: '#D13438',
        dataSource: {
            queryId: 'daily-abandon-stats'
        },
        config: { abandonSlaThreshold: 5 }
    },
    {
        id: 'widget-agent-status-stats',
        name: 'Statistiques de statut des agents',
        description: 'Durées de statut par agent (Prêt / Non prêt), connexion/déconnexion et stats d\'appels, groupées par queue',
        category: 'analytics',
        icon: FiUsers,
        defaultSize: { w: 12, h: 7 },
        type: 'agent-status-table',
        color: '#0078D4',
        dataSource: {
            queryId: 'agent-status-statistics'
        }
    },
    {
        id: 'widget-agent-status-breakdown',
        name: 'Statuts par agent',
        description: 'Barre empilée du temps passé dans chaque statut (%) par agent, groupée par queue',
        category: 'analytics',
        icon: FiUsers,
        defaultSize: { w: 6, h: 6 },
        type: 'agent-status-breakdown',
        color: '#0078D4',
        dataSource: {
            queryId: 'agent-status-statistics'
        }
    },
    {
        id: 'widget-ivr-option-distribution',
        name: 'Distribution des options IVR',
        description: 'Répartition des touches tapées (+ invalide / timeout / abandon) par menu IVR, groupée par flow',
        category: 'analytics',
        icon: FiPhone,
        defaultSize: { w: 6, h: 6 },
        type: 'ivr-option-distribution',
        color: '#0078D4',
        dataSource: {
            queryId: 'ivr-statistics'
        }
    },
    {
        id: 'widget-ivr-stats-table',
        name: 'Statistiques IVR par menu',
        description: 'Tableau par menu IVR : entrées, abandon %, invalide %, timeout %, replays et durée moyens, action principale',
        category: 'analytics',
        icon: FiPhone,
        defaultSize: { w: 12, h: 6 },
        type: 'ivr-stats-table',
        color: '#0078D4',
        dataSource: {
            queryId: 'ivr-statistics'
        }
    },
    {
        id: 'widget-ivr-funnel',
        name: 'Entonnoir IVR',
        description: 'Entonnoir par flow : entrées → option valide → routés, avec fuites (abandon / invalide / timeout)',
        category: 'analytics',
        icon: FiPhone,
        defaultSize: { w: 6, h: 6 },
        type: 'ivr-funnel',
        color: '#0078D4',
        dataSource: {
            queryId: 'ivr-statistics'
        }
    },

    // ========================================
    // WIDGETS PRÉSENTS DANS LES TEMPLATES MAIS ABSENTS DU CATALOGUE
    // ========================================
    // Ils étaient rendus sur les dashboards par défaut (src/Config/dashboardConfigs.js) sans
    // pouvoir être ajoutés à un dashboard custom : leur requête existait, l'entrée catalogue non.
    // ── RAPPEL EN FILE ──────────────────────────────────────────────────────────────────────
    //
    // Catégorie à part, et pas par cosmétique : la plupart des clients n'activent pas le rappel.
    // Dispersés dans « Metrics » au milieu de quarante autres, ces widgets seraient du bruit pour
    // eux ; regroupés, ils s'ignorent d'un coup d'œil et se trouvent quand on les cherche.
    {
        id: 'widget-callback-offered',
        name: 'Callbacks Offered',
        description: 'Calls where a callback was offered, as a share of all calls',
        category: 'callback',
        icon: FiPhone,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#1B3A6B',
        dataSource: { queryId: 'callback-offered-count' }
    },
    {
        id: 'widget-callback-accepted',
        name: 'Callbacks Accepted',
        description: 'Customers who accepted a callback, as a share of those offered one',
        category: 'callback',
        icon: FiCheckCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#00A4A6',
        dataSource: { queryId: 'callback-accepted-count' }
    },
    {
        id: 'widget-callback-on-time',
        name: 'On-Time Callback Rate',
        description: 'Share of callbacks connected before the deadline announced to the customer',
        category: 'callback',
        icon: FiTarget,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#00A4A6',
        dataSource: { queryId: 'callback-on-time-rate' },
        config: { format: 'percentage' }
    },
    {
        id: 'widget-callback-outcomes',
        name: 'Callback Outcomes',
        description: 'Breakdown of callback outcomes: completed, pending, failed',
        category: 'callback',
        icon: FiPieChart,
        defaultSize: { w: 4, h: 4 },
        type: 'donut',
        color: '#00A4A6',
        dataSource: { queryId: 'callback-outcome-distribution' },
        config: {
            nameKey: 'status',
            valueKey: 'count',
            categoryLabels: {
                callback_held: 'CALLBACK_HELD',
                callback_pending: 'CALLBACK_PENDING',
                callback_missed: 'CALLBACK_MISSED'
            },
            // Palette du portail (cf. widget-response-time-distribution dans
            // Config/dashboardConfigs.js) : teal = bon, navy = neutre, rouge = mauvais.
            // Les couleurs Fluent d'origine (#107C10 / #B26A00) sortaient du thème.
            categoryColors: {
                callback_held: '#00A4A6',
                callback_pending: '#1B3A6B',
                callback_missed: '#D13438'
            }
        }
    },
    {
        id: 'widget-callback-delay',
        name: 'Average Callback Delay',
        description: 'Actual time between the customer entering the queue and being connected',
        category: 'callback',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#F97316',
        dataSource: { queryId: 'callback-average-delay' },
        config: { format: 'duration' }
    },
    {
        id: 'widget-callback-attempts',
        name: 'Attempts per Callback',
        description: 'Average dial attempts per callback, successful and failed alike',
        category: 'callback',
        icon: FiRepeat,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#8B5CF6',
        dataSource: { queryId: 'callback-attempts-average' }
    },
    // ── CANAL MAIL ──────────────────────────────────────────────────────────────────────────
    //
    // Catégorie à part pour la même raison que le rappel : le canal mail n'est ouvert que chez
    // les clients qui l'ont souscrit. Dispersées dans « Metrics », ces dix cartes seraient du
    // bruit pour tous les autres.
    //
    // Les DURÉES sont en `longDuration`, pas en `duration` : le format historique plafonne à
    // 24 h — un garde-fou écrit pour des durées d'appel — et rendrait « — » sur un délai de
    // résolution mail, qui se compte normalement en jours.
    {
        id: 'widget-mail-total',
        name: 'Mails Handled',
        description: 'Number of mail cases closed over the period',
        category: 'mail',
        icon: FiMail,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#1B3A6B',
        dataSource: { queryId: 'mail-total' },
        config: { format: 'number' }
    },
    {
        id: 'widget-mail-queue-time',
        name: 'Average Mail Queue Time',
        description: 'Average wait in queue, from arrival to assignment to an agent',
        category: 'mail',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#F97316',
        dataSource: { queryId: 'mail-average-queue-time' },
        config: { format: 'longDuration' }
    },
    {
        id: 'widget-mail-handle-time',
        name: 'Average Mail Handle Time',
        description: 'Time the case spent on an agent desk, from opening to closing',
        category: 'mail',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#00A4A6',
        dataSource: { queryId: 'mail-average-handle-time' },
        config: { format: 'longDuration' }
    },
    {
        id: 'widget-mail-resolution-time',
        name: 'Average Mail Resolution Time',
        description: 'Whole life of the case, from the mail arriving to the case being closed',
        category: 'mail',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#8B5CF6',
        dataSource: { queryId: 'mail-average-resolution-time' },
        config: { format: 'longDuration' }
    },
    {
        id: 'widget-mail-first-response',
        name: 'Average First Response',
        description: 'Average delay before the customer gets a first reply — cases closed without a reply are excluded',
        category: 'mail',
        icon: FiCornerUpLeft,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#00A4A6',
        dataSource: { queryId: 'mail-average-first-response' },
        config: { format: 'longDuration' }
    },
    {
        id: 'widget-mail-sla-rate',
        name: 'Mail SLA Rate',
        description: 'Share of cases answered within the first-response deadline; cases with a fallback deadline are excluded',
        category: 'mail',
        icon: FiTarget,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#00A4A6',
        dataSource: { queryId: 'mail-sla-rate' },
        config: { format: 'percentage' }
    },
    {
        id: 'widget-mail-reopen-rate',
        name: 'Mail Reopen Rate',
        description: 'Share of cases reopened at least once — a customer writing back says the answer did not suffice',
        category: 'mail',
        icon: FiRepeat,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#D13438',
        dataSource: { queryId: 'mail-reopen-rate' },
        config: { format: 'percentage' }
    },
    {
        id: 'widget-mail-volume-daily',
        name: 'Mail Volume (Daily)',
        description: 'Mail cases closed per day',
        category: 'mail',
        icon: FiTrendingUp,
        defaultSize: { w: 6, h: 4 },
        type: 'line',
        color: '#00A4A6',
        dataSource: { queryId: 'mail-volume-by-day' },
        config: { xAxisKey: 'date', yAxisKey: 'mailCount' }
    },
    // Les colonnes de ces deux tableaux sont DÉCLARÉES et non déduites. L'auto-génération
    // range `agentName` dans ses `metadataKeys` — la colonne disparaîtrait — et prendrait
    // `agentId` comme libellé de ligne, affichant des GUID. Elle n'applique par ailleurs aucun
    // format : `dur_handle_avg` sortirait en secondes brutes à côté de cartes en hh:mm:ss.
    {
        id: 'widget-mail-per-agent',
        name: 'Mail per Agent',
        description: 'Volume, average handle time and SLA breaches per agent',
        category: 'mail',
        icon: FiUsers,
        defaultSize: { w: 8, h: 5 },
        type: 'table',
        color: '#1B3A6B',
        dataSource: { queryId: 'mail-per-agent' },
        config: {
            columns: [
                { key: 'agentName', nameKey: 'AGENT', name: 'Agent' },
                { key: 'mailCount', nameKey: 'MAIL_COUNT', name: 'Mails', width: 100, format: 'number' },
                { key: 'dur_handle_avg', nameKey: 'MAIL_AVG_HANDLE', name: 'Avg Handle', width: 120, format: 'longDuration' },
                { key: 'slaBreached', nameKey: 'MAIL_SLA_BREACHED', name: 'SLA Breached', width: 120, format: 'number' }
            ]
        }
    },
    {
        id: 'widget-mail-by-queue',
        name: 'Mail by Queue',
        description: 'Volume, average queue time and SLA breaches per queue',
        category: 'mail',
        icon: FiInbox,
        defaultSize: { w: 8, h: 5 },
        type: 'table',
        color: '#1B3A6B',
        dataSource: { queryId: 'mail-by-queue' },
        config: {
            columns: [
                { key: 'queueName', nameKey: 'QUEUE', name: 'Queue' },
                { key: 'mailCount', nameKey: 'MAIL_COUNT', name: 'Mails', width: 100, format: 'number' },
                { key: 'dur_queue_avg', nameKey: 'MAIL_AVG_QUEUE', name: 'Avg Queue Time', width: 140, format: 'longDuration' },
                { key: 'slaBreached', nameKey: 'MAIL_SLA_BREACHED', name: 'SLA Breached', width: 120, format: 'number' }
            ]
        }
    },
    // ── CANAL CHAT ──────────────────────────────────────────────────────────────────────────
    //
    // UNE MISE EN GARDE VAUT POUR TOUTE LA CATÉGORIE. Le CDR chat n'est écrit qu'à la
    // DÉCONNEXION d'une session établie : un client qui ferme l'onglet en attendant en file ne
    // laisse aucune trace. Ces cartes comptent donc des sessions ABOUTIES, pas des
    // sollicitations, et il n'existe volontairement pas de widget « taux d'abandon du chat » —
    // il serait faux. « Chats Closed Without an Agent » compte les clôtures hors horaires,
    // c'est autre chose et sa description le dit.
    {
        id: 'widget-chat-total',
        name: 'Chat Sessions',
        description: 'Number of completed chat sessions over the period',
        category: 'chat',
        icon: FiMessageSquare,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#1B3A6B',
        dataSource: { queryId: 'chat-total' },
        config: { format: 'number' }
    },
    {
        id: 'widget-chat-queue-time',
        name: 'Average Chat Queue Time',
        description: 'Average wait before an agent takes the session, across every queue it went through',
        category: 'chat',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#F97316',
        dataSource: { queryId: 'chat-average-queue-time' },
        config: { format: 'longDuration' }
    },
    {
        id: 'widget-chat-duration',
        name: 'Average Chat Duration',
        description: 'Average conversation time with the agent, from pickup to disconnect',
        category: 'chat',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#00A4A6',
        dataSource: { queryId: 'chat-average-duration' },
        config: { format: 'longDuration' }
    },
    {
        id: 'widget-chat-session-duration',
        name: 'Average Chat Session Duration',
        description: 'Whole life of the session, from the first message to the bot until disconnect',
        category: 'chat',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#8B5CF6',
        dataSource: { queryId: 'chat-average-session-duration' },
        config: { format: 'longDuration' }
    },
    {
        id: 'widget-chat-total-messages',
        name: 'Chat Messages',
        description: 'Messages exchanged over the period, agent and customer alike',
        category: 'chat',
        icon: FiMessageCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#1B3A6B',
        dataSource: { queryId: 'chat-total-messages' },
        config: { format: 'number' }
    },
    {
        id: 'widget-chat-average-messages',
        name: 'Average Messages per Chat',
        description: 'Average number of messages per session, both directions counted',
        category: 'chat',
        icon: FiMessageCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#00A4A6',
        dataSource: { queryId: 'chat-average-messages' },
        config: { format: 'number' }
    },
    {
        id: 'widget-chat-transfer-rate',
        name: 'Chat Transfer Rate',
        description: 'Share of queued sessions that went through more than one queue',
        category: 'chat',
        icon: FiRepeat,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#F97316',
        dataSource: { queryId: 'chat-transfer-rate' },
        config: { format: 'percentage' }
    },
    {
        id: 'widget-chat-without-agent',
        name: 'Chats Closed Without an Agent',
        description: 'Share of sessions closed before any agent took them — typically out-of-hours closures, not abandons',
        category: 'chat',
        icon: FiXCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#D13438',
        dataSource: { queryId: 'chat-without-agent' },
        config: { format: 'percentage' }
    },
    {
        id: 'widget-chat-volume-daily',
        name: 'Chat Volume (Daily)',
        description: 'Chat sessions per day',
        category: 'chat',
        icon: FiTrendingUp,
        defaultSize: { w: 6, h: 4 },
        type: 'line',
        color: '#00A4A6',
        dataSource: { queryId: 'chat-volume-by-day' },
        config: { xAxisKey: 'date', yAxisKey: 'chatCount' }
    },
    {
        id: 'widget-chat-per-agent',
        name: 'Chat per Agent',
        description: 'Sessions, messages and average conversation time per agent',
        category: 'chat',
        icon: FiUsers,
        defaultSize: { w: 8, h: 5 },
        type: 'table',
        color: '#1B3A6B',
        dataSource: { queryId: 'chat-per-agent' },
        config: {
            columns: [
                { key: 'agentName', nameKey: 'AGENT', name: 'Agent' },
                { key: 'chatCount', nameKey: 'CHAT_COUNT', name: 'Sessions', width: 100, format: 'number' },
                { key: 'messageCount', nameKey: 'CHAT_MESSAGES', name: 'Messages', width: 100, format: 'number' },
                { key: 'dur_conversation_avg', nameKey: 'CHAT_AVG_CONVERSATION', name: 'Avg Conversation', width: 150, format: 'longDuration' }
            ]
        }
    },
    {
        id: 'widget-chat-by-queue',
        name: 'Chat by Queue',
        description: 'Sessions and average queue wait per queue',
        category: 'chat',
        icon: FiInbox,
        defaultSize: { w: 8, h: 5 },
        type: 'table',
        color: '#1B3A6B',
        dataSource: { queryId: 'chat-by-queue' },
        config: {
            columns: [
                { key: 'queueName', nameKey: 'QUEUE', name: 'Queue' },
                { key: 'chatCount', nameKey: 'CHAT_COUNT', name: 'Sessions', width: 100, format: 'number' },
                { key: 'dur_queue_avg', nameKey: 'CHAT_AVG_QUEUE', name: 'Avg Queue Time', width: 140, format: 'longDuration' }
            ]
        }
    },
    {
        id: 'widget-chat-by-bot',
        name: 'Chat by Bot',
        description: 'Sessions and messages per entry chatbot — which bot brings the traffic',
        category: 'chat',
        icon: FiActivity,
        defaultSize: { w: 6, h: 5 },
        type: 'table',
        color: '#8B5CF6',
        dataSource: { queryId: 'chat-by-bot' },
        config: {
            columns: [
                { key: 'botName', nameKey: 'BOT', name: 'Bot' },
                { key: 'chatCount', nameKey: 'CHAT_COUNT', name: 'Sessions', width: 100, format: 'number' },
                { key: 'messageCount', nameKey: 'CHAT_MESSAGES', name: 'Messages', width: 100, format: 'number' }
            ]
        }
    },
    // ── CLASSIFICATION D'APPEL ──────────────────────────────────────────────────────────────
    //
    // LE TAUX SE LIT EN PREMIER, et les autres cartes n'ont de sens qu'à travers lui : une
    // répartition calculée sur 12 % des appels ne dit rien du centre, elle dit ce qu'ont bien
    // voulu saisir quelques agents. C'est pourquoi « Taux de classement » ouvre la catégorie.
    //
    // Le dénominateur est « appels TRAITÉS », jamais « tous les appels » : un appel abandonné
    // n'a pas eu d'agent, personne n'a PU le classer. L'inclure ferait chuter le taux à chaque
    // pic d'abandon — l'indicateur mesurerait la charge, pas la rigueur.
    {
        id: 'widget-classification-rate',
        name: 'Classification Rate',
        description: 'Share of handled calls the agent filed into a category',
        category: 'classification',
        icon: FiTarget,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#00A4A6',
        dataSource: { queryId: 'classification-rate' },
        config: { format: 'percentage' }
    },
    {
        id: 'widget-classification-distribution',
        name: 'Classification Distribution',
        description: 'Breakdown of classified calls by top-level category',
        category: 'classification',
        icon: FiPieChart,
        defaultSize: { w: 4, h: 4 },
        type: 'donut',
        color: '#1B3A6B',
        dataSource: { queryId: 'classification-distribution' },
        config: { nameKey: 'category', valueKey: 'count' }
    },
    {
        id: 'widget-classification-detail',
        name: 'Classification Detail',
        description: 'Breakdown by full path — the reading that tells you whether the tree itself needs reworking',
        category: 'classification',
        icon: FiList,
        defaultSize: { w: 8, h: 5 },
        type: 'table',
        color: '#8B5CF6',
        dataSource: { queryId: 'classification-detail' },
        config: {
            columns: [
                { key: 'path', nameKey: 'CLASSIFICATION_PATH', name: 'Category path' },
                { key: 'count', nameKey: 'CALLS', name: 'Calls', width: 100, format: 'number' },
                { key: 'percentage', nameKey: 'SHARE', name: 'Share', width: 100, format: 'percentage' }
            ]
        }
    },
    {
        id: 'widget-classification-by-queue',
        name: 'Classification by Queue',
        description: 'Per queue: handled, classified, rate and dominant category — it names the team where the instruction is not getting through',
        category: 'classification',
        icon: FiTag,
        defaultSize: { w: 8, h: 5 },
        type: 'table',
        color: '#1B3A6B',
        dataSource: { queryId: 'classification-by-queue' },
        config: {
            columns: [
                { key: 'queueName', nameKey: 'QUEUE', name: 'Queue' },
                { key: 'handled', nameKey: 'HANDLED_CALLS', name: 'Handled', width: 100, format: 'number' },
                { key: 'classified', nameKey: 'CLASSIFIED_CALLS', name: 'Classified', width: 100, format: 'number' },
                { key: 'classificationRate', nameKey: 'CLASSIFICATION_RATE', name: 'Rate', width: 100, format: 'percentage' },
                { key: 'topCategory', nameKey: 'TOP_CATEGORY', name: 'Top category', width: 160 }
            ]
        }
    },
    {
        id: 'widget-classification-by-agent',
        name: 'Classification by Agent',
        description: 'Per agent: handled, classified and rate',
        category: 'classification',
        icon: FiUsers,
        defaultSize: { w: 8, h: 5 },
        type: 'table',
        color: '#1B3A6B',
        dataSource: { queryId: 'classification-by-agent' },
        config: {
            columns: [
                { key: 'agentName', nameKey: 'AGENT', name: 'Agent' },
                { key: 'handled', nameKey: 'HANDLED_CALLS', name: 'Handled', width: 100, format: 'number' },
                { key: 'classified', nameKey: 'CLASSIFIED_CALLS', name: 'Classified', width: 100, format: 'number' },
                { key: 'classificationRate', nameKey: 'CLASSIFICATION_RATE', name: 'Rate', width: 100, format: 'percentage' }
            ]
        }
    },
    {
        id: 'widget-ivr-abandons',
        name: 'IVR Abandons',
        description: 'Calls abandoned in the IVR, before ever entering a queue',
        category: 'metrics',
        icon: FiXCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#D13438',
        dataSource: { queryId: 'ivr-abandon-count' }
    },
    {
        id: 'widget-handle-time',
        name: 'Average Handle Time (AHT)',
        description: 'Average handle time: talk + hold + wrap-up',
        category: 'metrics',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#0078D4',
        dataSource: { queryId: 'average-handle-time' },
        // Valeur renvoyée en SECONDES : sans ce format, la carte affichait le nombre brut
        // et l'export Excel sortait « 69 » là où le tableau KPI voisin lit « 00:01:09 ».
        config: { format: 'duration' }
    },
    {
        id: 'widget-talk-time',
        name: 'Average Talk Time',
        description: 'Average agent-caller conversation time',
        category: 'metrics',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#0078D4',
        dataSource: { queryId: 'average-talk-time' },
        // Valeur renvoyée en SECONDES : sans ce format, la carte affichait le nombre brut
        // et l'export Excel sortait « 69 » là où le tableau KPI voisin lit « 00:01:09 ».
        config: { format: 'duration' }
    },
    {
        id: 'widget-avg-abandon-time',
        name: 'Average Abandon Time',
        description: 'Average wait time before an abandon',
        category: 'metrics',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#D13438',
        dataSource: { queryId: 'avg-abandon-time' },
        // Valeur renvoyée en SECONDES : sans ce format, la carte affichait le nombre brut
        // et l'export Excel sortait « 69 » là où le tableau KPI voisin lit « 00:01:09 ».
        config: { format: 'duration' }
    },
    {
        id: 'widget-sla-trend',
        name: 'SLA Trend',
        description: 'Daily service level trend (answered within threshold / offered)',
        category: 'analytics',
        icon: FiTrendingUp,
        defaultSize: { w: 12, h: 5 },
        type: 'line',
        color: '#0078D4',
        dataSource: { queryId: 'sla-trend' },
        config: { xAxisKey: 'date', yAxisKey: 'slaValue', yAxisDomain: [0, 100] }
    },

    // ========================================
    // REQUÊTES BACKEND QUI N'ÉTAIENT EXPOSÉES NULLE PART
    // ========================================
    // Implémentées dans Data_Pipe (src/analyticsQueryCosmos.js) mais sans aucune entrée côté
    // portail : inatteignables autrement qu'en appelant l'API à la main.
    {
        id: 'widget-abandonment-rate',
        name: 'Abandonment Rate',
        description: '% of offered calls abandoned by the caller',
        category: 'metrics',
        icon: FiXCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#D13438',
        dataSource: { queryId: 'abandonment-rate' }
    },
    {
        id: 'widget-answer-rate',
        name: 'Answer Rate',
        description: '% of offered calls answered by an agent',
        category: 'metrics',
        icon: FiCheckCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#107C10',
        dataSource: { queryId: 'answer-rate' }
    },
    {
        id: 'widget-average-call-duration',
        name: 'Average Call Duration',
        description: 'Average total call duration, all directions',
        category: 'metrics',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#498205',
        dataSource: { queryId: 'average-call-duration' },
        // Valeur renvoyée en SECONDES : sans ce format, la carte affichait le nombre brut
        // et l'export Excel sortait « 69 » là où le tableau KPI voisin lit « 00:01:09 ».
        config: { format: 'duration' }
    },
    {
        id: 'widget-avg-ringing-time',
        name: 'Average Ringing Time',
        description: 'Average time an agent device rings before answer',
        category: 'metrics',
        icon: FiClock,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#FF8C00',
        dataSource: { queryId: 'avg-ringing-time' },
        // Valeur renvoyée en SECONDES : sans ce format, la carte affichait le nombre brut
        // et l'export Excel sortait « 69 » là où le tableau KPI voisin lit « 00:01:09 ».
        config: { format: 'duration' }
    },
    {
        id: 'widget-avg-agents-per-call',
        name: 'Agents Alerted per Call',
        description: 'Average number of agents rung per call (RONA + answering agent)',
        category: 'metrics',
        icon: FiUsers,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#8764B8',
        dataSource: { queryId: 'avg-agents-per-call' }
    },
    {
        id: 'widget-quick-drop-count',
        name: 'Quick Drops',
        description: 'Calls dropped in queue below the quick-drop threshold',
        category: 'metrics',
        icon: FiXCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#FF8C00',
        dataSource: { queryId: 'quick-drop-count' }
    },
    {
        id: 'widget-quick-drop-rate',
        name: 'Quick Drop Rate',
        description: '% of offered calls dropped below the quick-drop threshold',
        category: 'metrics',
        icon: FiXCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#FF8C00',
        dataSource: { queryId: 'quick-drop-rate' }
    },
    {
        id: 'widget-overflow-rate',
        name: 'Overflow Rate',
        description: '% of calls that overflowed out of their first queue',
        category: 'metrics',
        icon: FiTrendingUp,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#F7A600',
        dataSource: { queryId: 'overflow-rate' }
    },
    {
        id: 'widget-overflow-matrix',
        name: 'Overflow Matrix',
        description: 'Queue-to-queue overflow matrix: one row and one column per queue',
        category: 'analytics',
        icon: FiGrid,
        defaultSize: { w: 12, h: 6 },
        type: 'table',
        color: '#F7A600',
        dataSource: { queryId: 'overflow-matrix' }
    },
    {
        id: 'widget-overflow-paths',
        name: 'Top Overflow Paths',
        description: 'Twenty most frequent queue traversals (Queue A → Queue B → …)',
        category: 'analytics',
        icon: FiTrendingUp,
        defaultSize: { w: 6, h: 6 },
        type: 'table',
        color: '#F7A600',
        dataSource: { queryId: 'overflow-paths' }
    },

    // Nouveaux types de charts
    {
        id: 'widget-area-chart',
        name: 'Area Chart',
        description: 'Area chart with multiple series',
        category: 'charts',
        icon: FiTrendingUp,
        defaultSize: { w: 6, h: 4 },
        type: 'area',
        color: '#0078D4'
    },
    {
        id: 'widget-scatter-plot',
        name: 'Scatter Plot',
        description: 'Scatter plot for correlation analysis',
        category: 'charts',
        icon: FiActivity,
        defaultSize: { w: 6, h: 4 },
        type: 'scatter',
        color: '#8764B8'
    },
    {
        id: 'widget-radar-chart',
        name: 'Radar Chart',
        description: 'Multi-axis radar/spider chart',
        category: 'charts',
        icon: FiGrid,
        defaultSize: { w: 5, h: 5 },
        type: 'radar',
        color: '#0078D4'
    },
    {
        id: 'widget-heatmap-advanced',
        name: 'Advanced Heatmap',
        description: 'Color-coded data heatmap',
        category: 'charts',
        icon: FiActivity,
        defaultSize: { w: 8, h: 5 },
        type: 'heatmapAdvanced',
        color: '#D13438'
    },
    {
        id: 'widget-funnel-chart',
        name: 'Funnel Chart',
        description: 'Conversion funnel visualization',
        category: 'charts',
        icon: FiTrendingUp,
        defaultSize: { w: 5, h: 5 },
        type: 'funnel',
        color: '#107C10'
    },
    {
        id: 'widget-bullet-chart',
        name: 'Bullet Chart',
        description: 'Progress towards target',
        category: 'charts',
        icon: FiActivity,
        defaultSize: { w: 6, h: 3 },
        type: 'bullet',
        color: '#00BCF2'
    },
    {
        id: 'widget-treemap',
        name: 'TreeMap',
        description: 'Hierarchical data visualization',
        category: 'charts',
        icon: FiGrid,
        defaultSize: { w: 6, h: 5 },
        type: 'treemap',
        color: '#498205'
    },

    // Nouveaux widgets de métriques avancés
    {
        id: 'widget-kpi-comparison',
        name: 'KPI Comparison',
        description: 'Compare KPIs side by side',
        category: 'metrics',
        icon: FiActivity,
        defaultSize: { w: 4, h: 3 },
        type: 'kpiCompare',
        color: '#107C10'
    },
    {
        id: 'widget-progress-bar',
        name: 'Progress Bar',
        description: 'Visual progress indicator',
        category: 'metrics',
        icon: FiTrendingUp,
        defaultSize: { w: 4, h: 3 },
        type: 'progress',
        color: '#8764B8'
    },
    {
        id: 'widget-sparkline',
        name: 'Sparkline',
        description: 'Compact trend line',
        category: 'metrics',
        icon: FiTrendingUp,
        defaultSize: { w: 3, h: 2 },
        type: 'sparkline',
        color: '#0078D4'
    },
    {
        id: 'widget-status-indicator',
        name: 'Status Indicator',
        description: 'Real-time status monitoring',
        category: 'metrics',
        icon: FiActivity,
        defaultSize: { w: 3, h: 3 },
        type: 'status',
        color: '#107C10'
    },

    // Nouveaux widgets analytics avancés
    {
        id: 'widget-timeline',
        name: 'Timeline',
        description: 'Event timeline visualization',
        category: 'analytics',
        icon: FiClock,
        defaultSize: { w: 12, h: 4 },
        type: 'timeline',
        color: '#0078D4'
    },
    {
        id: 'widget-geographic-map',
        name: 'Geographic Map',
        description: 'Location-based data map',
        category: 'analytics',
        icon: FiGrid,
        defaultSize: { w: 8, h: 6 },
        type: 'geomap',
        color: '#00BCF2'
    },
    {
        id: 'widget-sankey',
        name: 'Sankey Diagram',
        description: 'Flow and movement visualization',
        category: 'analytics',
        icon: FiActivity,
        defaultSize: { w: 8, h: 6 },
        type: 'sankey',
        color: '#8764B8'
    },
    {
        id: 'widget-network-graph',
        name: 'Network Graph',
        description: 'Relationship network diagram',
        category: 'analytics',
        icon: FiGrid,
        defaultSize: { w: 8, h: 6 },
        type: 'network',
        color: '#498205'
    },
    {
        id: 'widget-waterfall-chart',
        name: 'Waterfall Chart',
        description: 'Cumulative effect visualization',
        category: 'analytics',
        icon: FiBarChart2,
        defaultSize: { w: 8, h: 5 },
        type: 'waterfall',
        color: '#FF8C00'
    },
    {
        id: 'widget-candlestick',
        name: 'Candlestick Chart',
        description: 'OHLC data visualization',
        category: 'analytics',
        icon: FiTrendingUp,
        defaultSize: { w: 8, h: 5 },
        type: 'candlestick',
        color: '#D13438'
    },

    // ═══ SURVEY — l'enquête jouée au client APRÈS le raccrochage de l'agent ═══
    //
    // ORDRE DE LECTURE. « Survey Rate » ouvre la catégorie parce qu'il conditionne tout le
    // reste : une satisfaction calculée sur 4 % des appels décrit quatre clients, pas le centre.
    // Vient ensuite le taux de complétion, qui dit ce que le client accepte de donner — les
    // mécontents raccrochent en cours d'enquête et sortent de la moyenne, si bien qu'un taux de
    // « oui » flatteur peut n'être que le reflet d'un questionnaire trop long.
    //
    // DEUX INDICATEURS DE RÉSULTAT, JAMAIS UN SEUL. Une question binaire rend un booléen, une
    // question notée rend un entier sur une échelle que le CDR ne porte pas. Les fondre dans un
    // unique « score de satisfaction » donnerait un nombre sans signification.
    {
        id: 'widget-survey-rate',
        name: 'Survey Rate',
        description: 'Share of handled calls offered a satisfaction survey',
        category: 'survey',
        icon: FiTarget,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#00A4A6',
        dataSource: { queryId: 'survey-rate' },
        config: { format: 'percentage' }
    },
    {
        id: 'widget-survey-completion-rate',
        name: 'Survey Completion Rate',
        description: 'Share of surveys where every question was answered',
        category: 'survey',
        icon: FiCheckCircle,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#107C10',
        dataSource: { queryId: 'survey-completion-rate' },
        config: { format: 'percentage' }
    },
    {
        id: 'widget-survey-yes-rate',
        name: 'Survey Yes Rate',
        description: 'Share of positive answers among answered yes/no questions',
        category: 'survey',
        icon: FiThumbsUp,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#1B3A6B',
        dataSource: { queryId: 'survey-yes-rate' },
        config: { format: 'percentage' }
    },
    {
        id: 'widget-survey-average-rating',
        name: 'Survey Average Rating',
        description: 'Average score among answered rating questions',
        category: 'survey',
        icon: FiStar,
        defaultSize: { w: 3, h: 2 },
        type: 'metric',
        color: '#FFB900',
        dataSource: { queryId: 'survey-average-rating' },
        // `number` et non un format « decimal » : MetricWidget n'en connaît pas, et il serait
        // tombé dans le défaut sans que rien ne le signale. Le défaut arrondit à une décimale,
        // ce qui est exactement ce qu'il faut pour une note moyenne.
        config: { format: 'number' }
    },
    {
        id: 'widget-survey-by-question',
        name: 'Survey by Question',
        description: 'Per question: asked, answered, answer rate and result',
        category: 'survey',
        icon: FiHelpCircle,
        defaultSize: { w: 8, h: 5 },
        type: 'table',
        color: '#1B3A6B',
        dataSource: { queryId: 'survey-by-question' },
        config: {
            columns: [
                { key: 'question', nameKey: 'QUESTION', name: 'Question' },
                { key: 'type', nameKey: 'TYPE', name: 'Type', width: 110 },
                { key: 'asked', nameKey: 'ASKED', name: 'Asked', width: 90, format: 'number' },
                { key: 'answered', nameKey: 'ANSWERED', name: 'Answered', width: 100, format: 'number' },
                { key: 'answerRate', nameKey: 'ANSWER_RATE', name: 'Answer rate', width: 110, format: 'percentage' },
                // Volontairement SANS format : `result` vaut un pourcentage de « oui » pour une
                // question binaire et une moyenne pour une note. Lui imposer l'un des deux
                // afficherait « 4,2 % » là où il faut lire « 4,2 sur 5 ». La colonne `type`
                // juste à côté est ce qui permet de lire la valeur pour ce qu'elle est.
                { key: 'result', nameKey: 'RESULT', name: 'Result', width: 100 }
            ]
        }
    },
    {
        id: 'widget-survey-keywords',
        name: 'Survey Keywords',
        description: 'Word cloud of what customers said in open answers',
        category: 'survey',
        icon: FiMessageCircle,
        defaultSize: { w: 6, h: 5 },
        type: 'word-cloud',
        color: '#1B3A6B',
        dataSource: { queryId: 'survey-keywords' },
        // 60 termes : au-delà, la queue de distribution n'apporte que des mots vus une fois,
        // qui remplissent le cadre sans rien dire et rapetissent tous les autres.
        config: { maxWords: 60 }
    },
    {
        id: 'widget-survey-detail',
        name: 'Survey Detail',
        description: 'One row per survey, to read the cases behind the averages',
        category: 'survey',
        icon: FiList,
        defaultSize: { w: 10, h: 5 },
        type: 'table',
        color: '#1B3A6B',
        dataSource: { queryId: 'survey-detail' },
        config: {
            columns: [
                { key: 'ts_survey_started', nameKey: 'STARTED_AT', name: 'Started', width: 110, format: 'clock' },
                { key: 'queue', nameKey: 'QUEUE', name: 'Queue', width: 140 },
                { key: 'agent', nameKey: 'AGENT', name: 'Agent', width: 150 },
                { key: 'dur_survey', nameKey: 'DURATION', name: 'Duration', width: 100, format: 'duration' },
                { key: 'asked', nameKey: 'ASKED', name: 'Asked', width: 80, format: 'number' },
                { key: 'answered', nameKey: 'ANSWERED', name: 'Answered', width: 100, format: 'number' },
                { key: 'yesCount', nameKey: 'YES_COUNT', name: 'Yes', width: 80, format: 'number' },
                { key: 'avgRating', nameKey: 'AVG_RATING', name: 'Avg rating', width: 110 }
            ]
        }
    }
];

const CATEGORIES = [
    { key: 'all', name: 'All Widgets', icon: FiGrid },
    { key: 'dashboard', name: 'Dashboard Defaults', icon: FiFolder },
    { key: 'metrics', name: 'Metrics', icon: FiActivity },
    { key: 'charts', name: 'Charts', icon: FiBarChart2 },
    { key: 'analytics', name: 'Analytics', icon: FiTrendingUp },
    { key: 'callback', name: 'Callback', icon: FiRotateCcw },
    { key: 'mail', name: 'Mail', icon: FiMail },
    { key: 'chat', name: 'Chat', icon: FiMessageSquare },
    { key: 'classification', name: 'Classification', icon: FiTag },
    { key: 'survey', name: 'Survey', icon: FiStar }
];

const WidgetCatalog = ({ isOpen, onDismiss, onSelectWidget }) => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');

    // Filtrer les widgets
    const filteredWidgets = WIDGET_CATALOG.filter(widget => {
        const matchesSearch = widget.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            widget.description.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || widget.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    // Grouper par catégorie
    const widgetsByCategory = filteredWidgets.reduce((acc, widget) => {
        if (!acc[widget.category]) {
            acc[widget.category] = [];
        }
        acc[widget.category].push(widget);
        return acc;
    }, {});

    const categoryLabels = {
        dashboard: 'Dashboard Defaults',
        metrics: 'Metrics',
        charts: 'Charts',
        analytics: 'Analytics Profile',
        callback: 'Callback',
        mail: 'Mail',
        chat: 'Chat',
        classification: 'Classification'
    };

    const handleWidgetClick = (widget) => {
        onSelectWidget(widget);
    };

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={onDismiss}
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: (
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                        <FiGrid size={24} />
                        <Text variant="xLarge">Widget Catalog</Text>
                    </Stack>
                ),
                subText: 'Click on a widget to add it to your dashboard'
            }}
            modalProps={{
                isBlocking: false,
                className: 'widget-catalog-dialog'
            }}
            minWidth="900px"
        >
            <div className="widget-catalog-container">
                {/* Barre de recherche et filtres */}
                <Stack tokens={{ childrenGap: 16 }} className="catalog-header">
                    <SearchBox
                        placeholder="Search widgets..."
                        value={searchQuery}
                        onChange={(_, newValue) => setSearchQuery(newValue || '')}
                        className="catalog-search"
                    />

                    {/* Onglets de catégories */}
                    <Stack horizontal tokens={{ childrenGap: 8 }} className="analytics-category-tabs">
                        {CATEGORIES.map(category => {
                            const Icon = category.icon;
                            return (
                                <div
                                    key={category.key}
                                    className={`analytics-category-tab ${selectedCategory === category.key ? 'active' : ''}`}
                                    onClick={() => setSelectedCategory(category.key)}
                                >
                                    <Icon size={18} />
                                    <span>{category.name}</span>
                                    <span className="widget-count">
                                        {category.key === 'all'
                                            ? WIDGET_CATALOG.length
                                            : WIDGET_CATALOG.filter(w => w.category === category.key).length
                                        }
                                    </span>
                                </div>
                            );
                        })}
                    </Stack>
                </Stack>

                {/* Grille de widgets */}
                <div className="widget-catalog-grid">
                    {selectedCategory === 'all' ? (
                        // Afficher tous les widgets groupés par catégorie
                        Object.entries(widgetsByCategory).map(([category, widgets]) => (
                            <div key={category} className="category-section">
                                <Text variant="large" className="category-title">
                                    {categoryLabels[category]}
                                </Text>
                                <div className="widget-grid">
                                    {widgets.map(widget => (
                                        <WidgetCard
                                            key={widget.id}
                                            widget={widget}
                                            onClick={() => handleWidgetClick(widget)}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))
                    ) : (
                        // Afficher uniquement la catégorie sélectionnée
                        <div className="widget-grid">
                            {filteredWidgets.map(widget => (
                                <WidgetCard
                                    key={widget.id}
                                    widget={widget}
                                    onClick={() => handleWidgetClick(widget)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {filteredWidgets.length === 0 && (
                    <div className="no-results">
                        <FiGrid size={48} />
                        <Text variant="large">No widgets found</Text>
                        <Text variant="small">Try adjusting your search or filters</Text>
                    </div>
                )}
            </div>

            <DialogFooter>
                <DefaultButton onClick={onDismiss} text="Close" />
            </DialogFooter>
        </Dialog>
    );
};

// Composant pour une carte de widget simplifié
const WidgetCard = ({ widget, onClick }) => {
    const { t } = useTranslation();
    const Icon = widget.icon;

    return (
        <div
            className="widget-card"
            onClick={onClick}
            style={{ borderLeftColor: widget.color }}
        >
            <div className="widget-card-header">
                <div className="widget-icon" style={{ backgroundColor: widget.color + '20' }}>
                    <Icon size={24} color={widget.color} />
                </div>
            </div>
            <div className="widget-card-body">
                <Text variant="medium" className="widget-name">{t(`WIDGET_NAMES.${widget.id}`, { defaultValue: widget.name })}</Text>
                <Text variant="small" className="widget-description">{t(`WIDGET_DESC.${widget.id}`, { defaultValue: widget.description })}</Text>
            </div>
            <div className="widget-card-footer">
                <span className="widget-type-badge">{widget.type}</span>
            </div>
        </div>
    );
};

export default WidgetCatalog;
